import zip from 'lodash/zip';
import { Database } from '@wix/da-papi-types/lib/supabase';
import { DamzState, MessageItem, type ReactionMap, RoomItem, RoomType } from '../types';
import { PapiUser } from '@wix/da-papi-types';
import { NON_CHAT_USERS, roomStreamIdPrefix, drawerStreamId } from '../constants';
import { makeAvatarUrl } from './helpers/avatar';
import { SupabaseClient } from '@supabase/supabase-js';
import { getContext } from 'redux-saga/effects';

export const DEFAULT_DRAFT_STATE: DamzState['drafts'] = {
  participants: [],
  messages: {},
  unfurlLog: {},
  unfurlResolving: false,
  deviationsNotFound: [],
};

/**
 * Generates an id for accessing a room's stream from redux.
 */
export function getRoomStreamId(room_id: string | undefined) {
  return `${roomStreamIdPrefix}${room_id}`;
}

export function getDrawerStreamId(filter: string) {
  return `${drawerStreamId}${filter}`;
}

export function getMessageCursor(messageId: string, sentAt: string) {
  return `${sentAt}|${messageId}`;
}

export function splitMessageCursor(cursor: string) {
  const [sentAt, messageId] = cursor.split('|');
  return {
    messageId,
    sentAt,
  };
}

export function getReactionMapFromMessage(message: MessageItem): ReactionMap {
  if (!message.msg.reactions) {
    return [];
  }

  const reactions = message.msg.reactions;
  const reactionMap: ReactionMap = [];

  // Sort reactions by order property
  const sortedEntries = Object.entries(reactions)
    .filter(([, reaction]) => reaction.count > 0)
    .sort((a, b) => a[1].order - b[1].order);

  for (const [emoji_id, reaction] of sortedEntries) {
    const emoji_id_num = parseInt(emoji_id, 10);
    if (emoji_id_num) {
      reactionMap.push(reaction);
    }
  }

  return reactionMap;
}

export function makePapiUser(username, uuid, userid_int, icon, symbol, banned): PapiUser {
  return {
    userId: userid_int,
    useridUuid: uuid,
    username,
    usericon: makeAvatarUrl(username, icon),
    type: banned ? 'banned' : (symbolToClass[symbol] ?? 'regular'),
    isNewDeviant: false,
    isBanned: banned,
  };
}

export function reactionUsersToPapiUsers(
  users: Database['damz']['Functions']['get_reaction_users']['Returns']
): PapiUser[] {
  return users.map(user =>
    makePapiUser(user.username, user.uuid, 0, user.usericon, user.symbol, user.banned_left)
  );
}

// Symbol to class mapping for user types
export const symbolToClass: Record<string, string> = {
  $: 'admin',
  '<': 'core_access',
  '*': 'core_plus',
  _: 'core_pro',
  '>': 'core_pro_plus',
  '|': 'core_max',
  '.': 'hell',
  '`': 'senior',
  '^': 'volunteer',
  '!': 'banned',
  '#': 'group',
  '%': 'group',
  '~': 'regular',
};

export function roomFetchToRoomItem(
  rows: Database['damz']['Functions']['get_conversations']['Returns']
): RoomItem[] {
  return rows.map(row => {
    const multiUsers = roomMultiUsersFromRow(row);
    return {
      room: {
        name: row.room_type === RoomType.DM ? undefined : row.name,
        room_type: row.room_type,
        room_id: row.room_id,
        updated_at: row.updated_at,
        owner_id: row.owner_id,
        user_count: row.user_count,
      },
      roomMeta: {
        flags: row.flags,
      },
      dmUser:
        row.room_type === RoomType.DM
          ? makePapiUser(
              row.dm_username,
              row.dm_user_id,
              row.dm_user_id_int,
              row.dm_usericon,
              row.dm_symbol,
              row.dm_user_banned
            )
          : undefined,
      multiUsers: row.room_type === RoomType.MULTI ? multiUsers : undefined,
      lastMsg:
        row.msg ||
        (row.msg_user_id && !NON_CHAT_USERS.some(user => `${user.userId}` === row.msg_user_id))
          ? {
              msg: row.msg,
              deleted_at: row.msg_deleted_at,
              user: makePapiUser(
                row.msg_username,
                row.msg_user_id,
                row.msg_user_id_int,
                row.msg_usericon,
                row.msg_symbol,
                row.msg_user_banned
              ),
            }
          : undefined,
    };
  });
}

function roomMultiUsersFromRow(
  row: Database['damz']['Functions']['get_conversations']['Returns'][0]
): RoomItem['multiUsers'] {
  const { multi_icon = '', multi_username = '' } = row;
  const usernameSplit = multi_username.split(',');
  const iconSplit = multi_icon.split(',').map(s => parseInt(s, 10));

  const returned: RoomItem['multiUsers'] = [];
  for (const [username, icon] of zip(usernameSplit, iconSplit)) {
    const avatarSrc = makeAvatarUrl(username, icon);
    if (username) {
      returned.push({ username, avatarSrc });
    }
  }
  return returned;
}

function unstringifyAttachments(
  message: Database['damz']['Functions']['get_messages']['Returns'][0]
) {
  return ['a', 'b', 'c', 'd', 'e', 'f'].reduce((accumulator, slot) => {
    const attachment = message[`${slot}_attachment`];
    return attachment
      ? { ...accumulator, [`${slot}_attachment`]: JSON.parse(attachment) }
      : accumulator;
  }, {});
}

function addOrderToReactions(
  reactions: Record<string, number>
): Record<string, { emoji_id: number; count: number; order: number }> {
  const orderedReactions: Record<string, { emoji_id: number; count: number; order: number }> = {};
  for (const [emoji_id, count] of Object.entries(reactions)) {
    orderedReactions[emoji_id] = {
      emoji_id: parseInt(emoji_id, 10),
      count,
      order: Date.now(),
    };
  }
  return orderedReactions;
}

export function messagesFetchToMessageItem(
  rows: Database['damz']['Functions']['get_messages']['Returns']
): MessageItem[] {
  return rows.map(row => ({
    msg: {
      ...row,
      ...unstringifyAttachments(row),
      reactions: addOrderToReactions((row.reactions as Record<string, number>) ?? {}),
    },
    user: makePapiUser(
      row.msg_username,
      row.msg_user_id,
      row.msg_user_id_int,
      row.msg_usericon,
      row.msg_symbol,
      row.msg_user_banned
    ),
  }));
}

export function createLastMsgFromSentMessage(
  messageText: string,
  user: PapiUser,
  messageId: string,
  deletedAt?: string
): RoomItem['lastMsg'] {
  return {
    msg: messageText,
    user,
    message_id: messageId,
    deleted_at: deletedAt,
  };
}

/**
 * Helper saga to fetch room data by room_id(s) using get_conversations_by_room_id.
 * Returns the data array or false on error.
 */
export function* fetchRoomById(room_ids: string | string[]) {
  const client: SupabaseClient<Database, 'damz'> = yield getContext('supaClient');
  const ids = Array.isArray(room_ids) ? room_ids : [room_ids];
  const response: {
    data: Database['damz']['Functions']['get_conversations_by_room_id']['Returns'] | null;
    error: any;
  } = yield client.rpc('get_conversations_by_room_id', {
    p_room_ids: ids,
  });
  if (response.error || !response.data || response.data.length === 0) {
    return false;
  }
  return response.data[0];
}
